import { useMemo, useState } from "react";
import { DEFAULT_PRESET, type DatePresetValue } from "#app/lib/utils/date-presets";
import { useCurrentUserFilterToggle } from "#app/lib/hooks/use-current-user-filter-toggle";
import type { DashboardScope } from "./dashboard-scope-context";

export type DashboardScopeState = {
  /** The assembled scope — feed this to `DashboardScopeProvider`. */
  scope: DashboardScope;
  dateRange: DatePresetValue;
  setDateRange: (next: DatePresetValue) => void;
  memberIds: string[];
  setMemberIds: (ids: string[]) => void;
  /** The Only-Me toggle machinery — wire straight into the toolbar. */
  currentUser: ReturnType<typeof useCurrentUserFilterToggle>;
};

/** The fixed entity facet a scoped dashboard reports on (e.g. one workflow or team). */
export type DashboardEntityScope = Pick<DashboardScope, "entity" | "entityWhere">;

/**
 * Owns the universal data window a dashboard's toolbar drives — date range,
 * member set, Only-Me — and assembles it into the {@link DashboardScope} the grid
 * publishes to every widget. A route holds this, feeds `scope` to
 * `DashboardScopeProvider`, and wires the setters into `DashboardFilterToolbar`.
 *
 * `entityScope` pins the dashboard to a single entity: its `entityWhere` is
 * AND-merged into every widget's query by `useScopedWhere`. The caller must
 * memoize `entityScope` so the published scope keeps a stable identity.
 *
 * Only-Me reuses the issue screens' `useCurrentUserFilterToggle` (the debounced
 * visual-vs-applied split + desktop/mobile press model), so the dashboard toggle
 * behaves identically to the one on the board and list.
 */
export function useDashboardScopeState(
  entityScope?: DashboardEntityScope | null,
  initialDateRange?: DatePresetValue,
): DashboardScopeState {
  const [dateRange, setDateRange] = useState<DatePresetValue>(
    initialDateRange ?? { key: DEFAULT_PRESET },
  );
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const currentUser = useCurrentUserFilterToggle();

  const scope = useMemo<DashboardScope>(
    () => ({
      entity: entityScope?.entity ?? null,
      entityWhere: entityScope?.entityWhere ?? null,
      dateRange,
      memberIds,
      onlyMe: currentUser.isCurrentUserFilterApplied,
    }),
    [entityScope, dateRange, memberIds, currentUser.isCurrentUserFilterApplied],
  );

  return { scope, dateRange, setDateRange, memberIds, setMemberIds, currentUser };
}
